import React, {
  RefObject,
  useMemo,
  useState,
  useCallback,
  SyntheticEvent,
} from 'react';
import clsx from 'clsx';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import IconButton from '@mui/material/IconButton';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import PinIcon from 'core/icons/pin';
import AutoCompletedInput from 'core/components/AutoComplete';
import Spinner from 'pages/Dashboard/components/Spinner';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Input from 'pages/Dashboard/components/Input';
import {
  useInsuranceSearch,
  useGetFavoriteInsurances,
} from 'views/EMR/components/Insurance/hooks';
import {
  Payer,
} from 'pages/Dashboard/types/insurance';
import {
  getAddressString,
} from 'utils/string';
import LabelWithActions from 'pages/Dashboard/components/Label/LabelWithActions';
import {
  InsuranceSearchType,
} from 'dtos/insuranceSearchType';
import {
  filterInsurances,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  getInsuranceBasicInfo,
} from 'utils/misc';

type InsuranceOptionProps = {
  option: Payer;
  props: Record<string, any>;
};

function InsuranceOption({
  option,
  props,
}: InsuranceOptionProps) {
  const basicInfo = useMemo(
    () => getInsuranceBasicInfo(option ?? {}),
    [option],
  );

  return (
    <div
      {...props}
      key={option.payerEntryId}
      className={clsx(
        props?.className,
        'flex flex-row !items-center !w-full gap-3',
      )}
    >
      {option.isFavorited && (
        <span>
          <PinIcon
            className="!text-base !text-blue-500"
          />
        </span>
      )}
      <div className="flex flex-col !items-start !justify-start !w-full !text-left gap-2">
        <span className="font-semibold">
          {option?.payerName ?? '--'}
        </span>
        <span className="text-xs text-gray-600">
          {basicInfo}
        </span>
        {!isNil(option.address) && (
          <span className="text-xs text-gray-600">
            {getAddressString(option.address)}
          </span>
        )}
      </div>
    </div>
  );
}

type Props = {
  className?: string;
  id: string;
  value?: Partial<Payer> | Payer[] | string | null;
  label?: string | null;
  placeholder?: string | null;
  readonly?: boolean;
  onChange: (value: Payer) => void;
  inputRef?: RefObject<HTMLInputElement>;
  resetOnEmpty?: boolean;
  required?: boolean;
  error?: boolean;
  filterOptions?: (options: Payer[]) => Payer[];
  labelClasses?: string;
  selectOnFocus?: boolean;
  multiple?: boolean;
  renderTags?: () => null;
  clearOnBlur?: boolean;
  insuranceType?: string;
  searchType?: InsuranceSearchType;
  toggleFavorites?: boolean;
};

type PayerIDProps = {
  value?: Props['value'];
};

function PayerID({ value }: PayerIDProps) {
  return (
    <ToolTip title="Payer ID">
      <span className="font-semibold text-xs cursor-pointer">
        {(value as Payer)?.payerId}
      </span>
    </ToolTip>
  );
}

function getInitialInputValue(value: Props['value']) {
  let initialInputValue = '';

  if (typeof value === 'string') {
    initialInputValue = value;
  } else if (!Array.isArray(value)) {
    initialInputValue = value?.payerName ?? '';
  }

  return initialInputValue;
}

export default function InsuranceLookup({
  className,
  id,
  onChange,
  placeholder = 'Enter Payer Name, Number, or Address...',
  value: _value,
  readonly,
  label = 'Payer',
  inputRef,
  required = false,
  error = false,
  filterOptions = (x) => x,
  labelClasses = 'text-gray-500',
  selectOnFocus = false,
  multiple = false,
  renderTags,
  clearOnBlur = false,
  insuranceType = undefined,
  searchType = 'All',
  toggleFavorites = true,
}: Props) {
  const initialInputValue = getInitialInputValue(_value);
  const [inputValue, setInputValue] = useState<string>(
    multiple ? '' : initialInputValue,
  );
  const [open, setOpen] = useState<boolean>(false);
  const value = useMemo(() => {
    let resolvedValue: Payer | Payer[] | null = null;

    if (multiple) {
      resolvedValue = Array.isArray(_value) ? _value : [];
    } else if (typeof _value === 'string') {
      resolvedValue = notEmptyString(_value)
        ? { payerName: _value } as Payer
        : null;
    } else {
      resolvedValue = (_value as Payer | null | undefined) ?? null;
    }

    return resolvedValue;
  }, [_value, multiple]);
  const [showFavorites, setShowFavorites] = useState(false);

  const classes = clsx('flex flex-col flex-auto', {
    '!space-y-0': isEmptyString(label),
    'space-y-1': !isEmptyString(label),
  });
  const containerClasses = clsx('flex flex-wrap w-full gap-4 mb-3', className);

  const { data: _options = [], isLoading } = useInsuranceSearch(
    insuranceType,
    inputValue,
    searchType,
    open && !isEmptyString(inputValue),
  );

  const { data: _favorites = [] } = useGetFavoriteInsurances();

  const options = showFavorites ? _favorites : _options;

  const handleChange = (value: Payer | Payer[]) => {
    setOpen(false);
    onChange(value as Payer);
  };

  const handleInputChange = useCallback(debounce((event: SyntheticEvent, newInputValue: string) => {
    if (open) {
      setInputValue(newInputValue);
    }
  }, 700), [open]);

  const noOptionsText = useMemo(() => (
    options.length === 0 && inputValue === ''
      ? 'Start typing to see the list'
      : 'No Insurance Found'
  ), [options, inputValue]);

  const isEmpty = useMemo(() => (
    isNil(value) || values(value).every(isEmptyString)
  ), [value]);
  const resolvedPlaceholder = isEmpty ? placeholder ?? '' : '';

  const handleToggleFavoritesList = () => {
    setShowFavorites((prev) => !prev);
    inputRef?.current?.focus();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const favoritesPinTooltipTitle = useMemo(() => {
    if (_favorites?.length > 0) {
      return 'Show pinned insurances';
    }
    return 'No pinned insurances are available';
  }, [_favorites]);

  return (
    <div className={containerClasses}>
      <Wrapper
        id="insurance-select"
        className={classes}
        required={required}
        error={error}
        showErrorText={false}
        label={!isNil(label) && (
          <LabelWithActions
            required={required}
            containerClassName="!items-end"
            id={id}
            label={label}
            className={labelClasses}
            actions={!readonly && toggleFavorites && (
              <ToolTip title={favoritesPinTooltipTitle}>
                <span>
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    color="info"
                    className="is-favorite disabled:opacity-50"
                    disabled={_favorites?.length === 0}
                    onClick={handleToggleFavoritesList}
                  >
                    <PinIcon
                      className={clsx('!text-sm text-gray-500', {
                        '!text-blue-500': showFavorites,
                      })}
                    />
                  </IconButton>
                </span>
              </ToolTip>
            )}
          />
        )}
      >
        {readonly
          ? (
            <Input
              id={id}
              onChange={noop}
              className="flex flex-1 space-y-1"
              inputProps={{ readOnly: true }}
              value={notEmptyString((value as Payer)?.payerName) ? (value as Payer)?.payerName : ''}
              endIcon={<PayerID value={value} />}
            />
          ) : (
            <AutoCompletedInput<Payer>
              value={value}
              isOptionEqualToValue={
                (option, value) => option.payerName === (value as Payer)?.payerName
              }
              id={id}
              disabled={readonly}
              options={options}
              onOpen={() => { setOpen(true); }}
              onClose={handleClose}
              open={open}
              multiple={multiple}
              getLabel={(option) => option?.payerName ?? ''}
              renderTags={renderTags}
              filterSelectedOptions={false}
              renderOption={(props: Record<string, any>, option: Payer) => (
                <InsuranceOption
                  key={option.payerEntryId}
                  props={props}
                  option={option}
                />
              )}
              loading={isLoading}
              inputRef={inputRef}
              openOnFocus
              includeInputInList
              className="w-full"
              onChange={handleChange}
              filterOptions={showFavorites ? filterInsurances : filterOptions}
              placeholder={resolvedPlaceholder}
              onInputChange={handleInputChange}
              classes={{ inputRoot: '!py-[7px]' }}
              clearOnBlur={clearOnBlur}
              selectOnFocus={selectOnFocus}
              blurOnSelect={false}
              noOptionsText={noOptionsText}
              dropdownClassName={clsx('!overflow-hidden !min-w-xs !max-w-md', {
                '!p-0': options?.length && isEmpty && !isLoading,
                'p-2': options?.length === 0,
              })}
              popupIcon={isLoading ? <Spinner /> : undefined}
              InputProps={{
                classes: { root: '!pr-2' },
                endAdornment: <PayerID value={value} />,
              }}
            />
          )}
      </Wrapper>
    </div>
  );
}
